import React, { useState } from 'react';
import { formatDistanceToNow, formatDistanceStrict } from 'date-fns';
import { answerPriorityDMQuestionAPI } from '../../../../../Service/user';
import './QuestionsPopup.scss';
import { toast } from 'react-hot-toast';

const QuestionsPopup = ({ questions: initialQuestions, timeline, onClose, communityId, dmId }) => {
  const [questions, setQuestions] = useState(initialQuestions);
  const [activeTab, setActiveTab] = useState('unanswered');
  const [answerText, setAnswerText] = useState('');
  const [answeringQuestionId, setAnsweringQuestionId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const answeredQuestions = questions.filter(q => q.isAnswered);
  const unansweredQuestions = questions.filter(q => !q.isAnswered);

  const getTimeLeft = (createdAt, timeline) => {
    const deadlineTime = new Date(createdAt).getTime() + (timeline * 60 * 1000);
    const timeLeft = deadlineTime - Date.now();
    
    if (timeLeft <= 0) return 'Time expired';
    return formatDistanceStrict(new Date(deadlineTime), new Date(), { addSuffix: true });
  };

  const handleAnswer = async (communityId, dmId, questionId, answerText) => {
    try {
      setIsSubmitting(true);
      const response = await answerPriorityDMQuestionAPI(communityId, dmId, questionId, answerText);
      
      // Update local state with answered question
      setQuestions(prev => prev.map(q => 
        q._id === questionId 
          ? { ...q, answer: answerText, isAnswered: true }
          : q
      ));
      
      setAnswerText('');
      setAnsweringQuestionId(null);
      toast.success("Answer submitted successfully!");
    } catch (error) {
      console.error("Error submitting answer:", error);
      toast.error("Failed to submit answer");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="questions-popup">
      <div className="popup-content">
        <div className="popup-header">
          <h2>Questions</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="questions-tabs">
          <button 
            className={`tab-button ${activeTab === 'unanswered' ? 'active' : ''}`}
            onClick={() => setActiveTab('unanswered')}
          >
            Unanswered ({unansweredQuestions.length})
          </button>
          <button 
            className={`tab-button ${activeTab === 'answered' ? 'active' : ''}`}
            onClick={() => setActiveTab('answered')}
          >
            Answered ({answeredQuestions.length})
          </button>
        </div>

        <div className="questions-list">
          {(activeTab === 'unanswered' ? unansweredQuestions : answeredQuestions).map((question, index) => (
            <div key={index} className="question-item">
              <div className="question-header">
                <div className="user-info">
                  <img src={question.userId.profilePicture || '/default-avatar.png'} alt="User" />
                  <span>{question.userId.firstName} {question.userId.lastName}</span>
                </div>
                <div className="time-info">
                  <span>Asked {formatDistanceToNow(new Date(question.createdAt), { addSuffix: true })}</span>
                  {!question.isAnswered && (
                    <span className="time-left">
                      Time to answer: {getTimeLeft(question.createdAt, timeline)}
                    </span>
                  )}
                </div>
              </div>

              <div className="question-content">
                <p>{question.question}</p>
                {question.isAnswered ? (
                  <div className="answer-section">
                    <strong>Answer:</strong>
                    <p>{question.answer}</p>
                  </div>
                ) : (
                  answeringQuestionId === question._id ? (
                    <div className="answer-form">
                      <textarea
                        value={answerText}
                        onChange={(e) => setAnswerText(e.target.value)}
                        placeholder="Type your answer..."
                      />
                      <div className="button-group">
                        <button 
                          className="cancel-button"
                          onClick={() => {
                            setAnsweringQuestionId(null);
                            setAnswerText('');
                          }}
                          disabled={isSubmitting}
                        >
                          Cancel
                        </button>
                        <button 
                          className="submit-button"
                          onClick={() => handleAnswer(communityId, dmId, question._id, answerText)}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? 'Submitting...' : 'Submit Answer'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button 
                      className="answer-button"
                      onClick={() => setAnsweringQuestionId(question._id)}
                    >
                      Answer
                    </button>
                  )
                )}
              </div>

              {question.payment && (
                <div className="payment-info">
                  <span>Payment: ₹{question.payment.amount}</span>
                  <span>Status: {question.payment.status}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default QuestionsPopup; 